import { getCSS, useTranslation } from '@gimlite/common';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { Suspense } from 'react';
import { createPortal } from 'react-dom';
import { Outlet } from 'react-router-dom';
import { useWindowSize } from 'usehooks-ts';
import { useDevice } from '../../hooks/useDevice.hook';
import { usePortalZIndex } from '../../hooks/usePortalZIndex';
import { useStoreUIV2 } from '../../hooks/useStore.hook';
import { MyAccount } from '../myAccount/myAccount.component';
import { Nav } from '../nav/nav.component';
import { Status } from '../status/status.component';
import { SwitchApp } from '../switchApp/switchApp.component';
import { Text } from '../text/text.component';
import './main.component.scss';
import { MyLangLocal } from '../myLangLocal/myLangLocal.component';

export declare namespace MainType {
  type Props = {
    onNavigate: (value: string) => void;
    logo?: React.ReactNode | null;
    mode?: 'dashboard' | 'free' | 'landing' | 'minimal';
    children?: React.ReactNode | null;
  };
}

const Banner = observer(() => {
  const { AppStore } = useStoreUIV2();

  return AppStore?.version?.env !== 'production' ? (
    <div
      style={{
        width: '100%',
        background: getCSS('--color-alert_warning'),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Text variant="caption">
        Environnement: {`${AppStore?.version?.env}`.toUpperCase()}
      </Text>
    </div>
  ) : null;
});

const MainMask = observer(() => {
  const { AppStore } = useStoreUIV2();
  const { t } = useTranslation();
  const { width, height } = useWindowSize();

  return !AppStore.isResponsive &&
    (width < AppStore.limitWidthResponsive ||
      height < AppStore.limitHeightResponsive) ? (
    <div className="mainMask">
      <Status
        icon="expand"
        backgroundColor="black_4"
        foregroundColor="black_50"
      />
      <div className="mainMask__content">
        <Text textAlign="center" variant="headline">
          {t('watermelon_RESOLUTION_MIN_TITLE')
            .replace('[WIDTH]', AppStore.limitWidthResponsive.toString())
            .replace('[HEIGHT]', AppStore.limitHeightResponsive.toString())}
        </Text>
        <Text textAlign="center" variant="body">
          {t('watermelon_RESOLUTION_MIN_DESCRIPTION')}
        </Text>
      </div>
    </div>
  ) : null;
});

export const Main = observer(
  ({ onNavigate, logo, mode = 'dashboard', children }: MainType.Props) => {
    const { SSOStore, MenuStore, WizardStore } = useStoreUIV2();
    const menu = MenuStore.menu;

    const wizards = WizardStore.instancesArray;

    const isWizardOpen = wizards && wizards.length > 0;
    const wizardZIndex = usePortalZIndex(isWizardOpen ?? false);
    const { isMobile } = useDevice();

    return mode === 'dashboard' ? (
      <main className="mainDash">
        <MainMask />
        <Banner />
        {createPortal(
          wizards && wizards.length > 0 && (
            <>
              {wizards.map((instance, index) => {
                const Component = instance.wizard.component;
                return (
                  <div
                    key={instance.instanceId}
                    style={{
                      zIndex: wizardZIndex ? wizardZIndex + index : undefined,
                    }}
                  >
                    <Suspense fallback={null}>
                      <Component wizardInstanceId={instance.instanceId} />
                    </Suspense>
                  </div>
                );
              })}
            </>
          ),
          document.body,
        )}
        <div className="mainDash__contain">
          <div className="mainDash__contain__items">
            <div className="mainDash__contain__items__nav">
              {menu.length > 0 && (
                <Nav logo={logo} onNavigate={onNavigate} data={menu} />
              )}
            </div>
            <div className="mainDash__contain__items__page">
              <div className="mainDash__contain__items__page__account">
                <MyAccount />
                {SSOStore.mode?.type === 'children' && <SwitchApp />}
              </div>
              {children || <Outlet />}
            </div>
            <div id="portal-right"></div>
          </div>
        </div>
      </main>
    ) : mode === 'free' ? (
      <main className="mainFree">
        <MainMask />
        <Banner />
        <div className="mainFree__contain">
          <div className="mainFree__contain__account">
            <div className="mainFree__contain__account__left">{logo}</div>
            <div className="mainFree__contain__account__right">
              <MyAccount />
              {SSOStore.mode?.type === 'children' && <SwitchApp />}
            </div>
          </div>

          {children || <Outlet />}
        </div>
      </main>
    ) : mode === 'landing' ? (
      <main className="mainLanding">
        <MainMask />
        <Banner />
        {createPortal(
          wizards && wizards.length > 0 && (
            <>
              {wizards.map((instance, index) => {
                const Component = instance.wizard.component;
                return (
                  <div
                    key={instance.instanceId}
                    style={{
                      zIndex: wizardZIndex ? wizardZIndex + index : undefined,
                    }}
                  >
                    <Suspense fallback={null}>
                      <Component wizardInstanceId={instance.instanceId} />
                    </Suspense>
                  </div>
                );
              })}
            </>
          ),
          document.body,
        )}
        <div className="mainLanding__contain">
          <div className="mainLanding__contain__page">
            <div className="mainLanding__contain__page__header">
              <div className="mainLanding__contain__page__header__logo">
                {logo}
              </div>
              <div className="mainLanding__contain__page__header__account">
                <MyAccount />
                {/* {SSOStore.mode?.type === 'children' && <SwitchApp />} */}
              </div>
            </div>
            {children || <Outlet />}
          </div>
        </div>
      </main>
    ) : mode === 'minimal' ? (
      <main
        className={classNames(
          'mainMinimal',
          isMobile ? 'mainMinimal--mobile' : 'mainMinimal--desktop',
        )}
      >
        <div className="mainMinimal__header">
          <div className="mainMinimal__header__left"></div>
          <div
            className="mainMinimal__header__logo"
            onClick={() => onNavigate('/')}
          >
            {logo}
          </div>
          <div className="mainMinimal__header__right">
            <MyLangLocal />
          </div>
        </div>

        {children || <Outlet />}
      </main>
    ) : null;
  },
);
